import React from "react";
import { useController, UseControllerProps, useFormContext } from "react-hook-form";
import { Switch, TextInput, TextInputProps, TextProps, TouchableOpacity } from "react-native";

import { formatPhoneNumber } from "src/util/gFormatting";
import { PdCheckbox, tPdCheckboxProps } from "../forms/checkbox";

import {
  PdDateRangeInput,
  PdInput,
  PdInputFloat,
  PdInputMultiple,
  PdInputNumber,
  PdInputPercentage,
  tPdInputFloatProps,
  tPdInputMultipleProps,
  tPdInputNumberProps,
  tPdInputPercentageProps,
  tPdInputProps,
  tPdRangeDateInputProps,
} from "../forms/input";
import { PdSelect, tPdSelectProps } from "../forms/select";

import { mergeRefs } from "../../utils";
import { PdImagePicker, tPdImagePickerProps } from "../forms/picker";
import { PdSwitch, tPdSwitchProps } from "../forms/switch";
import { PdFormErrorText } from "../forms/control";

export const PdHookFormInput = React.forwardRef<TextInput, Pick<UseControllerProps, "name" | "rules"> & tPdInputProps>(
  (props, ref) => {
    const { name, rules, value, onChangeText, ...rest } = props;
    const { control } = useFormContext();
    const { field } = useController({ name, rules, control });

    const handleOnChangeText = (text: string) => {
      field.onChange(text);
      onChangeText?.(text);
    };

    return (
      <PdInput
        value={field.value}
        onChangeText={handleOnChangeText}
        onBlur={field.onBlur}
        ref={mergeRefs([ref, field.ref])}
        {...rest}
      />
    );
  },
);

export const PdHookFormInputMultiple = React.forwardRef<
  TextInput,
  Pick<UseControllerProps, "name" | "rules"> & tPdInputMultipleProps
>((props, ref) => {
  const { name, rules, value, onChangeText, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnChangeText: tPdInputMultipleProps["onChangeText"] = (text) => {
    field.onChange(text);
    onChangeText?.(text);
  };

  return (
    <PdInputMultiple
      value={field.value}
      onChangeText={handleOnChangeText}
      onBlur={field.onBlur}
      ref={mergeRefs([ref, field.ref])}
      {...rest}
    />
  );
});

export const PdHookFormInputPhone = React.forwardRef<
  TextInput,
  Pick<UseControllerProps, "name" | "rules"> & TextInputProps
>((props, ref) => {
  const { name, rules, value, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });
  return (
    <PdInput
      value={formatPhoneNumber(field.value)}
      onChangeText={(newValue) => field.onChange(formatPhoneNumber(newValue))}
      onBlur={field.onBlur}
      ref={mergeRefs([ref, field.ref])}
      {...rest}
    />
  );
});

export const PdHookFormInputNumber = React.forwardRef<
  TextInput,
  Pick<UseControllerProps, "name" | "rules"> & Omit<tPdInputNumberProps, "value" | "onChangeText">
>((props, ref) => {
  const { name, rules, onChangeNumber, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnChange: tPdInputNumberProps["onChangeNumber"] = (value) => {
    field.onChange(value);
    onChangeNumber?.(value);
  };

  return (
    <PdInputNumber
      value={field.value}
      onChangeNumber={handleOnChange}
      onBlur={field.onBlur}
      ref={mergeRefs([ref, field.ref])}
      {...rest}
    />
  );
});

export const PdHookFormInputFloat = React.forwardRef<
  TextInput,
  Pick<UseControllerProps, "name" | "rules"> & Omit<tPdInputFloatProps, "value">
>((props, ref) => {
  const { name, rules, onChangeText, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnChange: tPdInputFloatProps["onChangeText"] = (value) => {
    field.onChange(value);
    onChangeText?.(value);
  };

  return (
    <PdInputFloat
      value={field.value}
      onChangeText={handleOnChange}
      onBlur={field.onBlur}
      ref={mergeRefs([ref, field.ref])}
      {...rest}
    />
  );
});

export const PdHookFormInputPercentage = React.forwardRef<
  TextInput,
  Pick<UseControllerProps, "name" | "rules"> & tPdInputPercentageProps
>((props, ref) => {
  const { value: _, name, rules, onChangeNumber, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnChangeNumber = (value: number | null) => {
    field.onChange(value);
    onChangeNumber?.(value);
  };

  return (
    <PdInputPercentage
      value={field.value}
      onChangeNumber={handleOnChangeNumber}
      onBlur={field.onBlur}
      ref={mergeRefs([ref, field.ref])}
      {...rest}
    />
  );
});

export const PdHookFormDateRangeInput = React.forwardRef<
  TextInput,
  Pick<UseControllerProps, "name" | "rules"> &
    Omit<tPdRangeDateInputProps, "period" | "onChange"> & { onChange?: tPdRangeDateInputProps["onChange"] }
>((props, ref) => {
  const { name, rules, onChange, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnChange: tPdRangeDateInputProps["onChange"] = (newPeriod, event) => {
    field.onChange(newPeriod);
    onChange?.(newPeriod, event);
  };

  return (
    <PdDateRangeInput period={field.value} onChange={handleOnChange} ref={mergeRefs([ref, field.ref])} {...rest} />
  );
});

export const PdHookFormSelect = React.forwardRef<
  TextInput,
  Pick<UseControllerProps, "name" | "rules"> & tPdSelectProps
>((props, ref) => {
  const { name, rules, value, onChange, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnChange: tPdSelectProps["onChange"] = (option) => {
    field.onChange(option);
    onChange?.(option);
  };

  return (
    <PdSelect
      value={field.value}
      onChange={handleOnChange}
      onBlur={field.onBlur}
      ref={mergeRefs([ref, field.ref])}
      {...rest}
    />
  );
});

export const PdHookFormSwitch = React.forwardRef<Switch, Pick<UseControllerProps, "name" | "rules"> & tPdSwitchProps>(
  (props, ref) => {
    const { name, rules, onValueChange, ...rest } = props;
    const { control } = useFormContext();
    const { field } = useController({ name, rules, control });

    const handleOnValueChange: tPdSwitchProps["onValueChange"] = (value) => {
      field.onChange(value);
      onValueChange?.(value);
    };

    return (
      <PdSwitch value={field.value} onValueChange={handleOnValueChange} ref={mergeRefs([ref, field.ref])} {...rest} />
    );
  },
);

export const PdHookFormCheckbox = React.forwardRef<
  TouchableOpacity,
  Pick<UseControllerProps, "name" | "rules"> & React.PropsWithChildren<tPdCheckboxProps>
>((props, ref) => {
  const { name, rules, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });
  return <PdCheckbox {...rest} ref={mergeRefs([ref, field.ref])} value={field.value} onChange={field.onChange} />;
});

export const PdHookFormImagePicker: React.FC<Pick<UseControllerProps, "name" | "rules"> & tPdImagePickerProps> = (
  props,
) => {
  const { name, rules, onChange, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnValueChange: tPdImagePickerProps["onChange"] = (value) => {
    field.onChange(value);
    onChange?.(value);
  };

  return <PdImagePicker {...rest} source={field.value} onChange={handleOnValueChange} />;
};

export const PdHookFormErrorMessage: React.FC<Pick<UseControllerProps, "name"> & TextProps> = ({ name, ...rest }) => {
  const { formState } = useFormContext();
  const formStateFieldError = formState.errors[name];

  if (!formStateFieldError) {
    return null;
  }

  if (Array.isArray(formStateFieldError)) {
    let messages = "";
    for (const fieldError of formStateFieldError) {
      if (!fieldError) continue;
      for (const fieldEntries of Object.entries<{ message: string }>(fieldError)) {
        messages += fieldEntries?.[1]?.message ? `${fieldEntries[1].message ?? ""}\n` : messages;
      }
    }

    return (
      <PdFormErrorText variant="body-xs" {...rest}>
        {messages}
      </PdFormErrorText>
    );
  }

  return (
    <PdFormErrorText variant="body-xs" {...rest}>
      {formStateFieldError.message}
    </PdFormErrorText>
  );
};
